import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  inject,
  input,
  output,
} from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalService } from 'src/app/_services/modal.service';
import lottie from 'lottie-web';
import { ErrorObject } from 'src/app/_models/error';
import { ButtonComponent } from '../../buttons/button/button.component';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-validation-modal',
    templateUrl: './validation-modal.component.html',
    styleUrls: ['./validation-modal.component.scss'],
    imports: [MatIcon, FormsModule, ReactiveFormsModule, ButtonComponent]
})
export class ValidationModalComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);

  private readonly element: HTMLElement;
  valueEmittedFromChildComponent = '';

  buttonIsDisabled = true;

  validationForm = this.fb.group({
    validationText: ['', [Validators.required]],
  });

  /** Modal id (required) */
  id = input<string>();

  /** Modal title (required) */
  titleText = input<string>();

  /** Validation text (required) */
  validationText = input<string>();

  /** Icon name (optional) */
  iconName = input<string | undefined>(undefined);

  /** Primary button text (required) */
  primaryButtonText = input<string>();

  /** Primary button id (required) */
  primaryButtonId = input<string>();

  /** Secondary button text (required) */
  secondaryButtonText = input<string>();

  /** Secondary button id (required) */
  secondaryButtonId = input<string>();

  /** Is the modal processing (optional, default: false) */
  isProcessing = input<boolean>(false);

  /** Does the modal have an animation? (optional, default: '') */
  animationType = input<'spinner' | 'test-tubes' | ''>('');

  /** Whether user can close modal (optional, default: true) */
  allowClose = input<boolean>(true);

  /** Error object (optional) */
  error = input<ErrorObject | undefined>(undefined);

  /** Output event for clicked button on modal */
  modalButtonClicked = output<string>();

  /**
   * @param {ElementRef} animationContainer Reference to html element containing the animation.
   * @required
   */
  @ViewChild('lottieContainer') animationContainer: ElementRef<HTMLElement>;

  constructor() {
    this.element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const modal = this;

    if (this.allowClose) {
      // Close modal on background click
      this.element.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.className === 'modal--container') {
          modal.close();
        }
      });
    }

    // add self (this modal instance) to the modal service so it's accessible from controllers
    this.modalService.add(this);

    // Now set validation rules for the modal validation form

    const validationControl = 'validationText';
    this.validationForm.controls[validationControl].valueChanges.subscribe(
      (value) => {
        const expected = this.validationText();
        if (value?.toLocaleLowerCase() === expected?.toLocaleLowerCase()) {
          this.buttonIsDisabled = false;
        } else {
          this.buttonIsDisabled = true;
        }
      },
    );
  }

  ngAfterViewInit(): void {
    if (this.animationType() === 'test-tubes') {
      this.initAnimation();
    }
  }

  initAnimation(): void {
    const animation = lottie.loadAnimation({
      container: this.animationContainer.nativeElement, // the dom element that will contain the animation
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: '../../assets/lottie/test-tube.json', // the path to the animation json
    });

    animation.play();
  }

  buttonEventHandler(valueEmitted: string): void {
    this.valueEmittedFromChildComponent = valueEmitted;
    this.modalButtonClicked.emit(valueEmitted);
  }

  // open modal
  open(): void {
    this.element.style.display = 'block';
  }

  // close modal
  close(): void {
    // Reset valdiation text value and close modal
    this.validationForm.reset();
    this.element.style.display = 'none';
  }

  // Remove self from modal service when component is destroyed
  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
